import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import AppModal from "../../../../Components/Common/AppModal";
import RichTextEditor from "../../../../Components/RichTextEditor";
import {
  systemChangeListApiCall,
  systemChangeCreateApiCall,
  systemChangeUpdateApiCall,
  systemChangeAssignBundleApiCall,
  systemChangeBundleCreateApiCall,
  systemChangeSyncApiCall,
} from "../../../../helpers/fakebackend_helper";
import ChangeDrawer from "./ChangeDrawer";
import BundlesTab from "./BundlesTab";
import LookupsTab from "./LookupsTab";
import {
  ColourChip,
  assigneeName,
  bundleLabel,
  formatDate,
  isDone,
  isReleased,
} from "./shared";

/**
 * System Changes (CRMit Task 24912).
 *
 * Every bug and improvement to CRMit. They used to be tasks in company 3 (our
 * own CRM company); they now live in ZCRM_Master and are managed here.
 *
 *   Open            not done yet
 *   Ready to bundle done (status marked Done) and not in a bundle yet
 *   Done            every done change, bundled or not
 *   All             everything
 *   Bundles         releases: done changes are put into one, then it is released
 *   Settings        statuses, types, priorities
 *
 * Migrated changes keep their company 3 task number. "Sync from company 3"
 * copies anything raised there since the migration (insert-only - nothing on
 * this side is ever overwritten).
 */

const TAB_OPEN = "open";
const TAB_READY = "ready";
const TAB_DONE = "done";
const TAB_ALL = "all";
const TAB_BUNDLES = "bundles";
const TAB_SETTINGS = "settings";

const DEFAULT_STATUS_ID = 46; // "CRMit: New", as Report a Bug uses

const EMPTY_CREATE = {
  Title: "",
  Description: "",
  SystemChangeStatus_Id: "",
  SystemChangeType_Id: "",
  SystemChangePriority_Id: "",
  AssignedUser_Id: "",
};

const SystemChanges = () => {
  const [changes, setChanges] = useState([]);
  const [lookups, setLookups] = useState({
    statuses: [],
    types: [],
    priorities: [],
    bundles: [],
    assignees: [],
  });
  const [loading, setLoading] = useState(false);
  const [notInstalled, setNotInstalled] = useState("");
  const [activeTab, setActiveTab] = useState(TAB_OPEN);

  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [pageSize, setPageSize] = useState(50);

  const [selection, setSelection] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [bundleRefreshKey, setBundleRefreshKey] = useState(0);
  // Change #100004 - Create Bundle from the header opens the Bundles tab's form.
  const [bundleCreateRequested, setBundleCreateRequested] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const [bulkBundleOpen, setBulkBundleOpen] = useState(false);
  const [bulkBundleId, setBulkBundleId] = useState("");
  const [newBundleName, setNewBundleName] = useState("");
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusId, setBulkStatusId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const [syncing, setSyncing] = useState(false);

  // Change #100026 - change a status straight from the list: the chip opens a
  // menu of statuses and the pick saves at once, no edit mode.
  const [statusMenu, setStatusMenu] = useState(null); // { anchorEl, row }

  const changeStatus = async (row, status) => {
    setStatusMenu(null);
    if (String(row.SystemChangeStatus_Id) === String(status.Id)) return;
    // Show it straight away; the reload afterwards brings back whatever the
    // server derived from it (Done tab, completed date).
    setChanges((prev) =>
      prev.map((change) =>
        String(change.Id) === String(row.Id)
          ? {
              ...change,
              SystemChangeStatus_Id: status.Id,
              SystemChangeStatus_Name: status.Name,
              SystemChangeStatus_CssColourBackground: status.CssColourBackground,
              SystemChangeStatus_CssColourText: status.CssColourText,
              SystemChangeStatus_FlagComplete: status.FlagComplete,
            }
          : change
      )
    );
    try {
      const response = await systemChangeUpdateApiCall([
        { Id: row.Id, SystemChangeStatus_Id: status.Id },
      ]);
      if (response?.status === 200) {
        toast.success(`#${row.Id} is now ${status.Name}`);
      } else {
        toast.error(response?.message || "Could not change the status");
      }
    } catch (e) {
      toast.error("Could not change the status");
    }
    refreshAll();
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await systemChangeListApiCall();
      if (response?.status === 200) {
        setNotInstalled("");
        setChanges(response.systemchange_list || []);
        setLookups({
          statuses: response.systemchangestatus_list || [],
          types: response.systemchangetype_list || [],
          priorities: response.systemchangepriority_list || [],
          bundles: response.systemchangebundle_list || [],
          assignees: response.assignee_list || [],
        });
      } else {
        // 409 until the database delta has been run.
        setNotInstalled(response?.message || "Could not load system changes");
      }
    } catch (e) {
      toast.error("Could not load system changes");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // Anything that changed a change also changes the bundles' counts.
  const refreshAll = () => {
    load();
    setBundleRefreshKey((key) => key + 1);
  };

  const tabRows = useMemo(() => {
    const term = keyword.trim().toLowerCase().replace(/^#/, "");
    const matches = (change) => {
      if (statusFilter && String(change.SystemChangeStatus_Id) !== String(statusFilter)) return false;
      if (typeFilter && String(change.SystemChangeType_Id) !== String(typeFilter)) return false;
      if (assigneeFilter && String(change.AssignedUser_Id) !== String(assigneeFilter)) return false;
      if (
        term &&
        !String(change.Id).startsWith(term) &&
        !String(change.Title || "").toLowerCase().includes(term)
      ) {
        return false;
      }
      return true;
    };

    const buckets = { [TAB_OPEN]: [], [TAB_READY]: [], [TAB_DONE]: [], [TAB_ALL]: [] };
    changes.forEach((change) => {
      if (!matches(change)) return;
      const row = { ...change, id: change.Id };
      buckets[TAB_ALL].push(row);
      if (isDone(change)) {
        buckets[TAB_DONE].push(row);
        if (!change.SystemChangeBundle_Id) buckets[TAB_READY].push(row);
      } else {
        buckets[TAB_OPEN].push(row);
      }
    });
    return buckets;
  }, [changes, keyword, statusFilter, typeFilter, assigneeFilter]);

  // Selection belongs to the grid it was made in.
  useEffect(() => {
    setSelection([]);
  }, [activeTab]);

  const openBundles = lookups.bundles.filter((bundle) => !isReleased(bundle));

  // ------------------------------------------------------------------
  // Create
  // ------------------------------------------------------------------

  const openCreate = () => {
    const hasDefault = lookups.statuses.some(
      (status) => Number(status.Id) === DEFAULT_STATUS_ID
    );
    setCreateForm({
      ...EMPTY_CREATE,
      SystemChangeStatus_Id: hasDefault ? DEFAULT_STATUS_ID : "",
    });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await systemChangeCreateApiCall([
        Object.fromEntries(
          Object.entries(createForm).map(([key, value]) => [key, value === "" ? null : value])
        ),
      ]);
      if (response?.status === 200) {
        toast.success(response.message);
        setCreateOpen(false);
        load();
        if (response.SystemChange_Id) setDetailId(response.SystemChange_Id);
      } else {
        toast.error(response?.message || "Could not create the change");
      }
    } catch (e) {
      toast.error("Could not create the change");
    }
    setCreating(false);
  };

  // ------------------------------------------------------------------
  // Bulk actions
  // ------------------------------------------------------------------

  const handleBulkBundle = async () => {
    setBulkBusy(true);
    try {
      let bundleId = bulkBundleId;
      // "New bundle" creates it first, then fills it.
      if (bundleId === "new") {
        const created = await systemChangeBundleCreateApiCall([{ Name: newBundleName.trim() }]);
        if (created?.status !== 200) throw new Error(created?.message || "Could not create the bundle");
        bundleId = created.SystemChangeBundle_Id;
      }
      const response = await systemChangeAssignBundleApiCall([
        { SystemChangeBundle_Id: bundleId || null, SystemChange_Ids: selection },
      ]);
      if (response?.status === 200) {
        toast.success(response.message);
        setBulkBundleOpen(false);
        setSelection([]);
        refreshAll();
      } else {
        toast.error(response?.message || "Could not update the bundle");
      }
    } catch (e) {
      toast.error(e?.message || "Could not update the bundle");
    }
    setBulkBusy(false);
  };

  const handleBulkStatus = async () => {
    setBulkBusy(true);
    try {
      const response = await systemChangeUpdateApiCall(
        selection.map((id) => ({ Id: id, SystemChangeStatus_Id: bulkStatusId }))
      );
      if (response?.status === 200) {
        toast.success(`${response.QtyUpdated || selection.length} change(s) updated`);
        setBulkStatusOpen(false);
        setSelection([]);
        refreshAll();
      } else {
        toast.error(response?.message || "Could not update the changes");
      }
    } catch (e) {
      toast.error("Could not update the changes");
    }
    setBulkBusy(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await systemChangeSyncApiCall();
      if (response?.status === 200) {
        const copied = (response.sync_steps || [])
          .filter((step) => ["Changes", "Notes", "Attachments"].includes(step.Step))
          .map((step) => `${step.RowsAffected} ${step.Step.toLowerCase()}`)
          .join(", ");
        toast.success(`Synced from company 3: ${copied || "nothing new"}`);
        refreshAll();
      } else {
        toast.error(response?.message || "Sync failed");
      }
    } catch (e) {
      toast.error("Sync failed");
    }
    setSyncing(false);
  };

  // ------------------------------------------------------------------
  // Grid
  // ------------------------------------------------------------------

  const columns = [
    { field: "Id", headerName: "#", width: 85 },
    {
      field: "Title",
      headerName: "Title",
      flex: 1,
      minWidth: 280,
      renderCell: (params) => {
        // Change #100028 - a person icon when someone is waiting to hear about
        // this change: blue while anyone is still to be told, grey once all are.
        const affected = Number(params.row.QtyAffected) || 0;
        const pending = Number(params.row.QtyAffectedPending) || 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            {affected > 0 && (
              <Tooltip
                title={
                  pending > 0
                    ? `${pending} of ${affected} still to tell when fixed`
                    : `All ${affected} told`
                }
              >
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}
                >
                  <PersonIcon fontSize="small" color={pending > 0 ? "primary" : "disabled"} />
                  {affected > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      {affected}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )}
            <Tooltip title={params.value || ""}>
              <Typography variant="body2" noWrap>
                {params.value}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "SystemChangeType_Name",
      headerName: "Type",
      width: 120,
      renderCell: (params) => (
        <ColourChip label={params.value} background={params.row.SystemChangeType_CssColourBackground} />
      ),
    },
    {
      field: "SystemChangePriority_Name",
      headerName: "Priority",
      width: 100,
      renderCell: (params) => (
        <ColourChip label={params.value} background={params.row.SystemChangePriority_CssColourBackground} />
      ),
    },
    {
      field: "SystemChangeStatus_Name",
      headerName: "Status",
      width: 230,
      // Change #100026 - click to change (see changeStatus).
      renderCell: (params) => (
        <Tooltip title="Change status">
          <Box
            component="button"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setStatusMenu({ anchorEl: event.currentTarget, row: params.row });
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              maxWidth: "100%",
              p: 0,
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              color: "text.secondary",
            }}
          >
            <ColourChip
              label={params.value || "Set status"}
              background={params.row.SystemChangeStatus_CssColourBackground}
              colour={params.row.SystemChangeStatus_CssColourText}
            />
            <ArrowDropDownIcon fontSize="small" />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "assignee",
      headerName: "Assigned",
      width: 140,
      valueGetter: (params) => assigneeName(params.row),
    },
    {
      // Change #100546 - "we need to show who they were raised by". The
      // reporter (Report a Bug / Server Errors) with their company, else
      // whoever created it. Changes migrated from company 3 have neither.
      field: "raisedBy",
      headerName: "Raised by",
      width: 170,
      valueGetter: (params) =>
        params.row.ReportedUser_FullName || params.row.CreatedUser_FullName || "",
      renderCell: (params) => {
        const company = params.row.ReportedCompany_BusinessName;
        if (!params.value && !company) return "-";
        return (
          <Tooltip title={[params.value, company].filter(Boolean).join(" - ")}>
            <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
              <Typography variant="body2" noWrap>
                {params.value || "-"}
              </Typography>
              {company && (
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {company}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "SystemChangeBundle_Name",
      headerName: "Bundle",
      width: 170,
      renderCell: (params) =>
        params.row.SystemChangeBundle_Id ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            {Number(params.row.SystemChangeBundle_FlagReleased) === 1 && (
              <LockIcon fontSize="inherit" color="success" />
            )}
            <Typography variant="body2" noWrap>
              {bundleLabel({
                Name: params.row.SystemChangeBundle_Name,
                VersionLabel: params.row.SystemChangeBundle_VersionLabel,
              })}
            </Typography>
          </Box>
        ) : (
          "-"
        ),
    },
    {
      field: "UpdatedDateTime",
      headerName: "Updated",
      width: 105,
      renderCell: (params) => formatDate(params.value),
    },
    {
      // Change #100027 - an explicit way into the side panel. Clicking the row
      // does it too, but with checkboxes on every row that is not obvious.
      field: "open",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Open">
          <IconButton
            size="small"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              setDetailId(params.row.Id);
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const isGridTab = [TAB_OPEN, TAB_READY, TAB_DONE, TAB_ALL].includes(activeTab);
  const rows = isGridTab ? tabRows[activeTab] : [];

  const lookupFilter = (label, value, setValue, options, optionLabel = (o) => o.Name) => (
    <TextField
      size="small"
      select
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      sx={{ minWidth: 150 }}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.Id} value={option.Id}>
          {optionLabel(option)}
        </MenuItem>
      ))}
    </TextField>
  );

  const usedStatusIds = new Set(changes.map((change) => String(change.SystemChangeStatus_Id)));

  return (
    <SettingsPanel
      title="System Changes"
      description="Every bug and improvement to CRMit, moved out of company 3 tasks into the master database. Done changes are put into a bundle, and a bundle is released as one."
      tabs={[
        { value: TAB_OPEN, label: "Open", count: tabRows[TAB_OPEN].length },
        { value: TAB_READY, label: "Ready to bundle", count: tabRows[TAB_READY].length },
        { value: TAB_DONE, label: "Done", count: tabRows[TAB_DONE].length },
        { value: TAB_ALL, label: "All", count: tabRows[TAB_ALL].length },
        { value: TAB_BUNDLES, label: "Bundles", count: openBundles.length || undefined },
        { value: TAB_SETTINGS, label: "Settings" },
      ]}
      value={activeTab}
      onChange={setActiveTab}
    >
      <Box sx={{ p: 3, width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold">
            System Changes
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Tooltip title="Copy anything raised in company 3 since the migration. Nothing here is overwritten.">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={handleSync}
                  disabled={syncing || !!notInstalled}
                >
                  {syncing ? "Syncing..." : "Sync from company 3"}
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={!!notInstalled}
            >
              Create Change
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => {
                setActiveTab(TAB_BUNDLES);
                setBundleCreateRequested(true);
              }}
              disabled={!!notInstalled}
            >
              Create Bundle
            </Button>
          </Box>
        </Box>

        {notInstalled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {notInstalled}
          </Alert>
        )}

        {isGridTab && (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search title or #number..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              {lookupFilter(
                "Status",
                statusFilter,
                setStatusFilter,
                lookups.statuses.filter(
                  (status) => Number(status.FlagActive) === 1 || usedStatusIds.has(String(status.Id))
                )
              )}
              {lookupFilter("Type", typeFilter, setTypeFilter, lookups.types)}
              {lookupFilter(
                "Assigned",
                assigneeFilter,
                setAssigneeFilter,
                lookups.assignees,
                (user) => user.UserFullName || user.Email
              )}
            </Box>

            {activeTab === TAB_READY && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Done and not yet in a bundle. Tick the changes going out together and use
                Add to bundle.
              </Alert>
            )}

            {selection.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {selection.length} selected
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Inventory2Icon />}
                  onClick={() => {
                    setBulkBundleId(openBundles[0]?.Id || "new");
                    setNewBundleName("");
                    setBulkBundleOpen(true);
                  }}
                >
                  Add to bundle
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setBulkStatusId("");
                    setBulkStatusOpen(true);
                  }}
                >
                  Set status
                </Button>
                <Button size="small" color="inherit" onClick={() => setSelection([])}>
                  Clear
                </Button>
              </Box>
            )}

            <Paper elevation={3} sx={{ height: 650, width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[25, 50, 100]}
                checkboxSelection
                disableSelectionOnClick
                selectionModel={selection}
                onSelectionModelChange={setSelection}
                onRowClick={(params) => setDetailId(params.row.Id)}
                sx={{ border: 0, "& .MuiDataGrid-row": { cursor: "pointer" } }}
              />
            </Paper>

            {/* Change #100026 - the status picker for one row. */}
            <Menu
              anchorEl={statusMenu?.anchorEl || null}
              open={!!statusMenu}
              onClose={() => setStatusMenu(null)}
              PaperProps={{ sx: { maxHeight: 420 } }}
            >
              {lookups.statuses
                .filter(
                  (status) =>
                    Number(status.FlagActive ?? 1) === 1 ||
                    String(status.Id) === String(statusMenu?.row?.SystemChangeStatus_Id)
                )
                .map((status) => {
                const current =
                  String(status.Id) === String(statusMenu?.row?.SystemChangeStatus_Id);
                return (
                  <MenuItem
                    key={status.Id}
                    selected={current}
                    onClick={() => changeStatus(statusMenu.row, status)}
                    sx={{ gap: 1 }}
                  >
                    <ColourChip
                      label={status.Name}
                      background={status.CssColourBackground}
                      colour={status.CssColourText}
                    />
                    {current && <CheckIcon fontSize="small" color="primary" />}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}

        {activeTab === TAB_BUNDLES && (
          <BundlesTab
            refreshKey={bundleRefreshKey}
            onChanged={load}
            onOpenChange={setDetailId}
            openCreate={bundleCreateRequested}
            onCreateOpened={() => setBundleCreateRequested(false)}
          />
        )}

        {activeTab === TAB_SETTINGS && <LookupsTab onChanged={load} />}

        <ChangeDrawer
          changeId={detailId}
          lookups={lookups}
          onClose={() => setDetailId(null)}
          onChanged={refreshAll}
        />

        {/* Create */}
        <AppModal
          open={createOpen}
          onClose={() => !creating && setCreateOpen(false)}
          title="Create a system change"
          onSubmit={handleCreate}
          submitLabel={creating ? "Creating..." : "Create"}
          submitDisabled={creating || !createForm.Title.trim()}
          loading={creating}
          size="md"
        >
          <TextField
            label="Title"
            value={createForm.Title}
            onChange={(e) => setCreateForm({ ...createForm, Title: e.target.value })}
            fullWidth
            autoFocus
            required
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            {[
              ["Type", "SystemChangeType_Id", lookups.types],
              ["Priority", "SystemChangePriority_Id", lookups.priorities],
              ["Status", "SystemChangeStatus_Id", lookups.statuses],
            ].map(([label, field, options]) => (
              <TextField
                key={field}
                select
                size="small"
                label={label}
                value={createForm[field]}
                onChange={(e) => setCreateForm({ ...createForm, [field]: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {options
                  .filter((option) => Number(option.FlagActive) === 1)
                  .map((option) => (
                    <MenuItem key={option.Id} value={option.Id}>
                      {option.Name}
                    </MenuItem>
                  ))}
              </TextField>
            ))}
            <TextField
              select
              size="small"
              label="Assigned to"
              value={createForm.AssignedUser_Id}
              onChange={(e) => setCreateForm({ ...createForm, AssignedUser_Id: e.target.value })}
            >
              <MenuItem value="">
                <em>Nobody</em>
              </MenuItem>
              {lookups.assignees.map((user) => (
                <MenuItem key={user.Id} value={user.Id}>
                  {user.UserFullName || user.Email}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {createOpen && (
            <RichTextEditor
              placeholder="Describe the change..."
              handleContentChange={(value) =>
                setCreateForm((form) => ({ ...form, Description: value }))
              }
            />
          )}
        </AppModal>

        {/* Add to bundle */}
        <AppModal
          open={bulkBundleOpen}
          onClose={() => !bulkBusy && setBulkBundleOpen(false)}
          title={`Add ${selection.length} change(s) to a bundle`}
          onSubmit={handleBulkBundle}
          submitLabel="Add"
          submitDisabled={bulkBusy || (bulkBundleId === "new" && !newBundleName.trim())}
          loading={bulkBusy}
        >
          <TextField
            select
            label="Bundle"
            value={bulkBundleId}
            onChange={(e) => setBulkBundleId(e.target.value)}
            fullWidth
          >
            {openBundles.map((bundle) => (
              <MenuItem key={bundle.Id} value={bundle.Id}>
                {bundleLabel(bundle)}
              </MenuItem>
            ))}
            <MenuItem value="new">
              <em>New bundle...</em>
            </MenuItem>
            <MenuItem value="">
              <em>Take them out of their bundle</em>
            </MenuItem>
          </TextField>
          {bulkBundleId === "new" && (
            <TextField
              label="New bundle name"
              value={newBundleName}
              onChange={(e) => setNewBundleName(e.target.value)}
              fullWidth
              autoFocus
              sx={{ mt: 2 }}
            />
          )}
        </AppModal>

        {/* Set status */}
        <AppModal
          open={bulkStatusOpen}
          onClose={() => !bulkBusy && setBulkStatusOpen(false)}
          title={`Set the status of ${selection.length} change(s)`}
          onSubmit={handleBulkStatus}
          submitLabel="Update"
          submitDisabled={bulkBusy || !bulkStatusId}
          loading={bulkBusy}
        >
          <TextField
            select
            label="Status"
            value={bulkStatusId}
            onChange={(e) => setBulkStatusId(e.target.value)}
            fullWidth
          >
            {lookups.statuses
              .filter((status) => Number(status.FlagActive) === 1)
              .map((status) => (
                <MenuItem key={status.Id} value={status.Id}>
                  {status.Name}
                  {Number(status.FlagComplete) === 1 ? "  (done)" : ""}
                </MenuItem>
              ))}
          </TextField>
        </AppModal>
      </Box>
    </SettingsPanel>
  );
};

export default SystemChanges;
